import React, { useState } from 'react';
import { Form, InputField } from '@trp-beacon-ui-kit/react-controls';
import FocusTrap from 'focus-trap-react';

import crossIcon from '../../images/cross-icon.svg';
import { dispatchOverlayLoad } from '../ModalCTA/utils';
import { sendLeadGenEmail } from './util';
import styles from './LeadGenForm.css';

const isValidEmail = (emailAddress) => {
    const emailRegex = /^[A-Za-z0-9._%-]+@[A-Za-z0-9.-]+\.[a-zA-Z]{2,4}$/;
    if (!emailAddress) return 'This field is required';
    if (!emailRegex.test(emailAddress))
        return 'This is not a valid email. Please try again.';
    return null;
};

const createTextFieldValidation = (fieldTitle) => (textToValidate) => {
    const nameRegex = /^[A-Za-z]*$/;
    if (!textToValidate) return 'This field is required';
    if (!nameRegex.test(textToValidate))
        return `Invalid character in ${fieldTitle}`;
    return null;
};

const LeadGenForm = ({ showInModal, onClose }) => {
    const [successfulSubmit, setSubmitState] = useState(null);
    const [displayLoading, setLoadingState] = useState(false);
    const [callCount, iterateCounter] = useState(0);

    const onSubmit = async (values) => {
        setLoadingState(true);

        const userSubmitted = await sendLeadGenEmail(values);

        setLoadingState(false);
        iterateCounter(callCount + 1);
        setSubmitState(userSubmitted);

        dispatchOverlayLoad(
            userSubmitted
                ? 'Lead Generation – Confirmation: Success'
                : 'Lead Generation – Confirmation: Failure'
        );
    };

    const handleClick = (e) => {
        if (!successfulSubmit) {
            setSubmitState(null);
            return;
        }
        e.preventDefault();
        window.location.href =
            'https://www.troweprice.com/personal-investing/planning-and-research/t-rowe-price-insights.html';
    };

    const handleErrors = (errors) => {
        const invalidInputNames = Object.keys(errors);
        let invalidInputID;

        if (invalidInputNames.includes('emailAddress')) {
            invalidInputID = 'email';
        }
        if (invalidInputNames.includes('lastName')) {
            invalidInputID = 'last_name';
        }
        if (invalidInputNames.includes('firstName')) {
            invalidInputID = 'first_name';
        }
        document.getElementById(invalidInputID).focus();
    };

    return (
        <React.Fragment>
            {/* Formerly a MODAL but now showing in div vs Popup */}

            <div>
                {successfulSubmit === null && (
                    <FocusTrap active={Boolean(showInModal)}>
                        <div className="modal-lead-gen">
                            {showInModal && (
                                <button
                                    className="close-btn"
                                    onClick={() => onClose()}
                                >
                                    <img
                                        className="close-btn-img"
                                        alt="Close"
                                        src={crossIcon}
                                    />
                                </button>
                            )}

                            <div className="modal-main-content">
                                <div className="title">
                                    T. Rowe Price Insights delivered to your
                                    inbox
                                </div>
                                <div>
                                    <p>
                                        Receive monthly retirement guidance,
                                        financial planning tips, and market
                                        updates to help you make confident
                                        decisions about your finances.
                                    </p>
                                </div>
                                <Form
                                    onSubmit={(errors, values) =>
                                        errors
                                            ? handleErrors(errors)
                                            : onSubmit(values)
                                    }
                                >
                                    {(submit) => (
                                        <div>
                                            <InputField
                                                id="first_name"
                                                name="firstName"
                                                aria-label="First Name"
                                                required
                                                type="text"
                                                title="First Name"
                                                autoComplete="given-name"
                                                validation={createTextFieldValidation(
                                                    'First Name'
                                                )}
                                                className="blocked_data"
                                            />
                                            <InputField
                                                id="last_name"
                                                name="lastName"
                                                aria-label="Last Name"
                                                required
                                                type="text"
                                                title="Last Name"
                                                autoComplete="family-name"
                                                validation={createTextFieldValidation(
                                                    'Last Name'
                                                )}
                                                className="blocked_data"
                                            />
                                            <InputField
                                                id="email"
                                                name="emailAddress"
                                                aria-label="Email"
                                                placeholder="email@domain.com"
                                                required
                                                type="email"
                                                title="Email"
                                                autoComplete="email"
                                                validation={isValidEmail}
                                                className="blocked_data"
                                            />
                                            <InputField
                                                id="no_email"
                                                name="noEmail"
                                                type="hidden"
                                                value="false"
                                            />
                                            <div
                                                className={
                                                    displayLoading
                                                        ? 'disabled'
                                                        : 'buttons'
                                                }
                                            >
                                                <button
                                                    className="text-button"
                                                    id="lead-submit"
                                                    onClick={submit}
                                                >
                                                    {displayLoading
                                                        ? 'Loading...'
                                                        : 'Subscribe Now'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </Form>
                            </div>
                            <div className="footer">
                                <p>
                                    By subscribing, you agree to receive
                                    occasional emails about our products and
                                    services. The&nbsp;
                                    <a href="https://www.troweprice.com/personal-investing/help/policies-and-security/privacy.html">
                                        Privacy
                                    </a>{' '}
                                    section of our Help Center has information
                                    about how to opt out or update your
                                    preferences. This offer is intended for
                                    persons in the United States and should not
                                    be considered a solicitation or offering of
                                    any investment products or services to
                                    investors residing outside the United
                                    States.
                                </p>
                            </div>
                        </div>
                    </FocusTrap>
                )}
                {/* RESPONSE MODAL */}
                {successfulSubmit && (
                    <FocusTrap active={Boolean(showInModal)}>
                        <div className="modal2">
                            {showInModal && (
                                <button
                                    className="close-btn"
                                    onClick={() => onClose()}
                                >
                                    <img
                                        className="close-btn-img"
                                        alt="Close"
                                        src={crossIcon}
                                    />
                                </button>
                            )}
                            <div
                                className="modal-main-content"
                                aria-live="assertive"
                                role="alert"
                            >
                                <div className="title">
                                    Thank you for subscribing
                                </div>

                                <p>
                                    {`Each month, we'll keep you connected with
                                    monthly retirement guidance, financial planning
                                    tips, and market updates.`}
                                </p>

                                <div className="buttons">
                                    <button
                                        className="text-button"
                                        id="insights"
                                        onClick={(e) => {
                                            handleClick(e);
                                        }}
                                    >
                                        Explore Insights Now
                                    </button>
                                </div>
                            </div>
                        </div>
                    </FocusTrap>
                )}
                {/* OOPS MODAL AFTER 2 ATTEMPTS */}
                {!successfulSubmit &&
                    successfulSubmit !== null && (
                        <FocusTrap active={Boolean(showInModal)}>
                            <div className="modal2">
                                {showInModal && (
                                    <button
                                        className="close-btn"
                                        onClick={() => onClose()}
                                    >
                                        <img
                                            className="close-btn-img"
                                            alt="Close"
                                            src={crossIcon}
                                        />
                                    </button>
                                )}

                                <div
                                    className="modal-main-content"
                                    aria-live="assertive"
                                    role="alert"
                                >
                                    <div className="title">
                                        Something went wrong
                                    </div>

                                    <p>
                                        {callCount >= 2
                                            ? `We’re sorry for the inconvenience. Please come back later to try again.`
                                            : 'Please try submitting again.'}
                                    </p>

                                    <div className="buttons">
                                        {callCount < 2 && (
                                            <button
                                                className="text-button"
                                                id="insights"
                                                onClick={(e) => {
                                                    handleClick(e);
                                                }}
                                            >
                                                Try Again
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </FocusTrap>
                    )}
            </div>

            <style jsx>{styles}</style>
        </React.Fragment>
    );
};

export default LeadGenForm;



// WEBPACK FOOTER //
// ./src/components/LeadGenForm/LeadGenForm.js